import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  AlertTriangle,
  Calendar,
  Clock,
  AlertCircle,
  Check,
  RefreshCw,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { timeAgo } from '@/components/layout/NotificationBell';
import type { Notification, NotificationType } from '@/components/layout/NotificationBell';

/* ==========================================================================
   S10 — Notifications page

   Full list of notifications with type filters and bulk mark-read.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface NotificationsResponse {
  data: Notification[];
  unreadCount: number;
}

type FilterTab = 'tutte' | 'non_lette' | NotificationType;

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const TYPE_CONFIG: Record<
  NotificationType,
  { Icon: typeof Bell; iconClass: string; bgClass: string; label: string }
> = {
  laser_inactive: {
    Icon: AlertTriangle,
    iconClass: 'text-amber-500',
    bgClass: 'bg-amber-50',
    label: 'Laser inattivo',
  },
  checkup_due: {
    Icon: Calendar,
    iconClass: 'text-status-info',
    bgClass: 'bg-status-info-light',
    label: 'Check-up da fare',
  },
  package_expiring: {
    Icon: Clock,
    iconClass: 'text-accent-primary',
    bgClass: 'bg-accent-primary-light',
    label: 'Pacchetto in scadenza',
  },
  service_missing: {
    Icon: AlertCircle,
    iconClass: 'text-status-negative',
    bgClass: 'bg-status-negative-light',
    label: 'Servizio mancante',
  },
};

const FILTER_TABS: Array<{ value: FilterTab; label: string }> = [
  { value: 'tutte', label: 'Tutte' },
  { value: 'non_lette', label: 'Non lette' },
  { value: 'laser_inactive', label: 'Laser' },
  { value: 'checkup_due', label: 'Check-up' },
  { value: 'package_expiring', label: 'Pacchetti' },
  { value: 'service_missing', label: 'Servizi' },
];

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Notifications() {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('tutte');
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  // ------------------------------------------------------------------
  // Load
  // ------------------------------------------------------------------

  const loadNotifications = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<NotificationsResponse>('/notifications');
      setNotifications(data?.data ?? []);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Errore di connessione. Riprova.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // ------------------------------------------------------------------
  // Mark single as read
  // ------------------------------------------------------------------

  const handleMarkRead = useCallback(async (notification: Notification) => {
    if (notification.readAt) return;
    try {
      await api.post(`/notifications/${notification.id}/read`);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id
            ? { ...n, readAt: new Date().toISOString() }
            : n,
        ),
      );
    } catch {
      // Ignore
    }
  }, []);

  // ------------------------------------------------------------------
  // Mark all read
  // ------------------------------------------------------------------

  const handleMarkAllRead = useCallback(async () => {
    setIsMarkingAll(true);
    try {
      await api.post('/notifications/read-all');
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          readAt: n.readAt ?? new Date().toISOString(),
        })),
      );
    } catch {
      // Ignore
    } finally {
      setIsMarkingAll(false);
    }
  }, []);

  // ------------------------------------------------------------------
  // Navigate to entity
  // ------------------------------------------------------------------

  const handleNotificationClick = useCallback(
    async (notification: Notification) => {
      await handleMarkRead(notification);
      if (notification.clientId) {
        navigate(`/clients/${notification.clientId}`);
      }
    },
    [handleMarkRead, navigate],
  );

  // ------------------------------------------------------------------
  // Filtered list
  // ------------------------------------------------------------------

  const filteredNotifications = notifications.filter((n) => {
    if (activeFilter === 'tutte') return true;
    if (activeFilter === 'non_lette') return !n.readAt;
    return n.type === activeFilter;
  });

  const unreadCount = notifications.filter((n) => !n.readAt).length;

  // ------------------------------------------------------------------
  // Header actions
  // ------------------------------------------------------------------

  const headerActions =
    unreadCount > 0 ? (
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<Check strokeWidth={2} />}
        onClick={handleMarkAllRead}
        loading={isMarkingAll}
      >
        Segna tutto
      </Button>
    ) : undefined;

  // ------------------------------------------------------------------
  // Loading state
  // ------------------------------------------------------------------

  if (isLoading) {
    return (
      <AppShell header={<Header showBack title="Notifiche" />}>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <NotificationSkeleton key={i} />
          ))}
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Error state
  // ------------------------------------------------------------------

  if (error) {
    return (
      <AppShell header={<Header showBack title="Notifiche" />}>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div
            className={cn(
              'mb-5 flex h-16 w-16 items-center justify-center',
              'rounded-full bg-status-negative-light',
            )}
          >
            <Bell className="h-7 w-7 text-status-negative" strokeWidth={1.5} />
          </div>
          <p className="text-lg font-[family-name:var(--font-heading)] font-semibold text-text-primary">
            {error}
          </p>
          <Button
            variant="secondary"
            size="md"
            className="mt-6"
            leftIcon={<RefreshCw strokeWidth={2} />}
            onClick={loadNotifications}
          >
            Riprova
          </Button>
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------

  return (
    <AppShell
      header={
        <Header
          showBack
          title={`Notifiche${unreadCount > 0 ? ` (${unreadCount})` : ''}`}
          actions={headerActions}
        />
      }
    >
      <div className="space-y-5 pb-8">
        {/* ---- Filter tabs ---- */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1">
          {FILTER_TABS.map((tab) => {
            const isActive = activeFilter === tab.value;
            return (
              <button
                key={tab.value}
                onClick={() => setActiveFilter(tab.value)}
                className={cn(
                  'shrink-0 px-4 py-2 rounded-[var(--radius-full)]',
                  'text-sm font-[family-name:var(--font-heading)] font-semibold',
                  'transition-all duration-[var(--duration-fast)]',
                  'cursor-pointer min-h-[36px]',
                  'active:scale-[0.97]',
                  isActive
                    ? 'bg-accent-primary text-text-inverted shadow-sm'
                    : 'bg-surface-card text-text-secondary border border-border-default hover:border-border-hover',
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ---- Empty state ---- */}
        {filteredNotifications.length === 0 && (
          <div className="flex flex-col items-center py-16 text-center">
            <div
              className={cn(
                'mb-4 flex h-14 w-14 items-center justify-center',
                'rounded-full bg-surface-sunken',
              )}
            >
              <Bell className="h-6 w-6 text-text-muted" strokeWidth={1.5} />
            </div>
            <p className="font-[family-name:var(--font-heading)] text-base font-semibold text-text-primary">
              Nessuna notifica
            </p>
            <p className="text-sm text-text-muted mt-1">
              {activeFilter === 'tutte'
                ? 'Non ci sono notifiche al momento.'
                : 'Nessuna notifica in questa categoria.'}
            </p>
          </div>
        )}

        {/* ---- Notification cards ---- */}
        <div className="space-y-2">
          {filteredNotifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onClick={() => handleNotificationClick(notification)}
              onMarkRead={() => handleMarkRead(notification)}
            />
          ))}
        </div>
      </div>
    </AppShell>
  );
}

/* --------------------------------------------------------------------------
   NotificationCard
   -------------------------------------------------------------------------- */

function NotificationCard({
  notification,
  onClick,
  onMarkRead,
}: {
  notification: Notification;
  onClick: () => void;
  onMarkRead: () => void;
}) {
  const config = TYPE_CONFIG[notification.type] ?? TYPE_CONFIG.checkup_due;
  const { Icon, iconClass, bgClass, label } = config;
  const isUnread = !notification.readAt;

  return (
    <div
      className={cn(
        'relative flex items-start gap-4',
        'rounded-[var(--radius-lg)] border px-5 py-4',
        'transition-all duration-[var(--duration-fast)]',
        isUnread
          ? 'bg-surface-card border-border-default shadow-xs'
          : 'bg-surface-base border-transparent',
        notification.clientId && 'cursor-pointer hover:border-border-hover hover:shadow-sm',
      )}
      onClick={notification.clientId ? onClick : undefined}
      role={notification.clientId ? 'button' : undefined}
    >
      {/* Unread indicator */}
      {isUnread && (
        <div
          className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-accent-primary"
          aria-hidden
        />
      )}

      {/* Type icon */}
      <div
        className={cn(
          'flex items-center justify-center h-10 w-10 rounded-full shrink-0',
          bgClass,
        )}
      >
        <Icon className={cn('h-5 w-5', iconClass)} strokeWidth={1.5} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p
                className={cn(
                  'text-sm font-[family-name:var(--font-heading)]',
                  isUnread
                    ? 'font-bold text-text-primary'
                    : 'font-semibold text-text-secondary',
                )}
              >
                {notification.title}
              </p>
              <span
                className={cn(
                  'text-xs px-2 py-0.5 rounded-full',
                  bgClass,
                  iconClass,
                  'font-medium',
                )}
              >
                {label}
              </span>
            </div>
            <p className="text-sm text-text-muted mt-1 leading-relaxed">
              {notification.body}
            </p>
          </div>

          {/* Time + mark read */}
          <div className="shrink-0 flex flex-col items-end gap-2">
            <span className="text-xs text-text-muted whitespace-nowrap">
              {timeAgo(notification.createdAt)}
            </span>
            {isUnread && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkRead();
                }}
                className={cn(
                  'flex items-center justify-center h-7 w-7',
                  'rounded-[var(--radius-sm)]',
                  'text-text-muted hover:text-text-primary',
                  'hover:bg-surface-sunken',
                  'transition-colors cursor-pointer',
                )}
                aria-label="Segna come letta"
                title="Segna come letta"
              >
                <Check className="h-4 w-4" strokeWidth={2} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   Skeleton
   -------------------------------------------------------------------------- */

function NotificationSkeleton() {
  return (
    <div className="flex items-start gap-4 rounded-[var(--radius-lg)] border border-border-default bg-surface-card px-5 py-4">
      <Skeleton height={40} className="w-10 rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton height={14} className="w-3/5" />
        <Skeleton height={12} className="w-4/5" />
        <Skeleton height={12} className="w-2/5" />
      </div>
    </div>
  );
}
